import { useState, useEffect, useRef } from "react";
import { Share } from "@capacitor/share";

const SERVER_URL = "https://myofficer.duckdns.org";
const WS_URL = "wss://myofficer.duckdns.org";
const NAVER_CLIENT_ID = "dzbs9hoked";

window.__serverUrl = 'https://myofficer.duckdns.org';

// ── FCM 푸시 알림 초기화 (Capacitor 환경에서만 동작) ──
async function initPushNotifications(onToken) {
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    
    // 권한 확인 및 요청
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== "granted") return;

    // FCM 등록
    await PushNotifications.register();

    // FCM 토큰 수신
    PushNotifications.addListener("registration", (token) => {
      window.__fcmToken = token.value;
      onToken(token.value);
      // 토큰 받은 시점에 이미 방에 있으면 즉시 서버에 등록
      registerFcmToken(token.value, window.__currentRoomCode, window.__currentUserId);
    });

    PushNotifications.addListener("registrationError", (err) => {
      console.error("[FCM] 등록 실패:", err);
    });

    // 포그라운드 알림 수신 시 토스트로 표시
    PushNotifications.addListener("pushNotificationReceived", (notification) => {
      if (window.__showToast) {
        window.__showToast("📢 " + (notification.title || "") + ": " + (notification.body || ""));
      }
    });

    // 알림 탭해서 앱 열었을 때
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      console.log("[FCM] 알림 탭:", action);
    });
  } catch (e) {
    // 웹 환경에선 무시
  }
}


let naverMapPromise = null;

function loadNaverMap() {
  if (window.naver && window.naver.maps) return Promise.resolve(window.naver.maps);
  if (naverMapPromise) return naverMapPromise;
  naverMapPromise = new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = "https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=" + NAVER_CLIENT_ID;
    s.async = true;
    let d = false;
    const ti = setTimeout(() => { if (!d) { d = true; rej(new Error("timeout")); } }, 10000);
    s.onload = () => {
      if (!d) {
        d = true;
        clearTimeout(ti);
        if (window.naver && window.naver.maps) {
          res(window.naver.maps);
        } else {
          rej(new Error("naver maps not found"));
        }
      }
    };
    s.onerror = () => { if (!d) { d = true; clearTimeout(ti); rej(new Error("error")); } };
    document.head.appendChild(s);
  });
  return naverMapPromise;
}

const COLORS = ["#00e5ff", "#ff6b35", "#00ff88", "#ffd700", "#ff4488", "#a78bfa", "#fb923c", "#34d399"];
const PIN_TYPES = [
  { id: "warn", icon: "⚠️", label: "주의", color: "#ffd700" },
  { id: "block", icon: "🚧", label: "차단", color: "#ff6b35" },
  { id: "check", icon: "✅", label: "완료", color: "#00ff88" },
  { id: "info", icon: "📌", label: "정보", color: "#00e5ff" },
  { id: "alert", icon: "🚨", label: "긴급", color: "#ff4488" },
];
const QUICK_MSGS = ["지금 이동합니다", "현장 도착", "지원 요청", "상황 종료", "주의 요망"];
const ULSAN = { lat: 35.5384, lng: 129.3114 };
const G = {
  surface: "#0e1118", surface2: "#12151c", surface3: "#1a1e28",
  border: "#252a38", accent: "#00e5ff", green: "#00ff88", dim: "#4b5563", text: "#e8eaf0",
};

function genId() { return Math.random().toString(36).slice(2, 10); }
// FCM 토큰을 서버에 등록하는 함수 (재시도 포함)
function registerFcmToken(token, roomCode, userId) {
  if (!token || !roomCode || !userId) return;
  fetch('https://myofficer.duckdns.org/fcm-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, roomCode, userId }),
  }).catch(() => {});
}

// 디바이스 고유 ID - localStorage에 저장해서 재접속 시 동일 ID 사용
function getDeviceId() {
  try {
    let id = localStorage.getItem('_deviceId');
    if (!id) { id = genId() + genId(); localStorage.setItem('_deviceId', id); }
    return id;
  } catch (e) { return genId(); }
}
function genCode() { return Math.random().toString(36).slice(2, 8).toUpperCase(); }
function hashPwd(p) {
  let h = 0;
  for (let i = 0; i < p.length; i++) h = (Math.imul(31, h) + p.charCodeAt(i)) | 0;
  return h.toString(16);
}
function teamColor(t) {
  if (!t || !t.trim()) return COLORS[0];
  let h = 0;
  for (let i = 0; i < t.length; i++) h = (Math.imul(31, h) + t.charCodeAt(i)) | 0;
  return COLORS[Math.abs(h) % COLORS.length];
}

const TILE = 256;
function latToWorldY(lat, z) {
  const s = Math.sin(lat * Math.PI / 180);
  return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * TILE * Math.pow(2, z);
}
function lngToWorldX(lng, z) { return (lng + 180) / 360 * TILE * Math.pow(2, z); }
function worldYToLat(y, z) {
  const n = Math.PI - 2 * Math.PI * y / (TILE * Math.pow(2, z));
  return 180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}
function worldXToLng(x, z) { return x / (TILE * Math.pow(2, z)) * 360 - 180; }

function toXY(lat, lng, cLat, cLng, zoom, w, h) {
  const cx = lngToWorldX(cLng, zoom);
  const cy = latToWorldY(cLat, zoom);
  return { x: lngToWorldX(lng, zoom) - cx + w / 2, y: latToWorldY(lat, zoom) - cy + h / 2 };
}
function fromXY(px, py, cLat, cLng, zoom, w, h) {
  const cx = lngToWorldX(cLng, zoom);
  const cy = latToWorldY(cLat, zoom);
  return { lat: worldYToLat(cy + (py - h / 2), zoom), lng: worldXToLng(cx + (px - w / 2), zoom) };
}

async function apiPost(path, body) {
  const res = await fetch(SERVER_URL + path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return res.json();
}
async function apiGet(path) {
  const res = await fetch(SERVER_URL + path);
  return res.json();
}

function useWS(code, myId, member, onMsg) {
  const wsRef = useRef(null);
  const retryRef = useRef(null);
  const memberRef = useRef(member);
  useEffect(() => { memberRef.current = member; }, [member]);

  useEffect(() => {
    if (!code) return;
    let stopped = false;

    function connect() {
      if (stopped) return;
      try {
        const ws = new WebSocket(WS_URL);
        ws.onopen = () => {
          const fcmToken = window.__fcmToken || null;
          ws.send(JSON.stringify({ type: "join", code: code, id: myId, member: memberRef.current, fcmToken }));
          // 25초마다 ping으로 연결 유지 (백그라운드 절전 방지)
          ws._heartbeat = setInterval(() => {
            if (ws.readyState === 1) {
              try { ws.send(JSON.stringify({ type: "ping" })); } catch (e) {}
            }
          }, 25000);
        };
        ws.onmessage = (e) => { try { onMsg(JSON.parse(e.data)); } catch (err) {} };
        ws.onclose = () => {
          clearInterval(ws._heartbeat);
          if (!stopped) retryRef.current = setTimeout(connect, 3000);
        };
        ws.onerror = () => { try { ws.close(); } catch (e) {} };
        wsRef.current = ws;
      } catch (e) {}
    }
    connect();

    return () => {
      stopped = true;
      clearTimeout(retryRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch (e) {} }
    };
  }, [code]);

  return function send(data) {
    if (wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify(data));
    }
  };
}

function NaverMap({ cLat, cLng, zoom, onReady, onError }) {
  const ref = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadNaverMap().then((maps) => {
      if (cancelled || !ref.current || mapRef.current) return;
      try {
        const map = new maps.Map(ref.current, {
          center: new maps.LatLng(cLat, cLng),
          zoom: Math.round(zoom),
          zoomControl: false,
          mapTypeControl: false,
          scaleControl: false,
          logoControl: false,
          mapDataControl: false,
          draggable: false,
          scrollWheel: false,
          keyboardShortcuts: false,
          pinchZoom: false,
          disableDoubleClickZoom: true,
          disableDoubleTapZoom: true,
        });
        mapRef.current = map;
        if (onReady) onReady();
      } catch (e) { if (onError) onError(e); }
    }).catch((e) => { if (onError) onError(e); });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        try { mapRef.current.destroy(); } catch (e) {}
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (mapRef.current && window.naver && window.naver.maps) {
      try {
        mapRef.current.setCenter(new window.naver.maps.LatLng(cLat, cLng));
        mapRef.current.setZoom(Math.round(zoom), false);
      } catch (e) {}
    }
  }, [cLat, cLng, zoom]);

  return (
    <div ref={ref} style={{ position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none", touchAction: "none" }} />
  );
}

function MapSVG({ members, pins, zoom, cLat, cLng, w, h, onMapClick, onPinClick, pinMode, hideGrid, myId }) {
  const sp = Math.max(30, 70 * (zoom / 13));
  const lines = [];
  if (!hideGrid) {
    for (let x = ((w / 2) % sp + sp) % sp; x < w; x += sp) {
      lines.push(<line key={"v" + x.toFixed(0)} x1={x} y1={0} x2={x} y2={h} stroke="#141820" strokeWidth={1} />);
    }
    for (let y = ((h / 2) % sp + sp) % sp; y < h; y += sp) {
      lines.push(<line key={"h" + y.toFixed(0)} x1={0} y1={y} x2={w} y2={y} stroke="#141820" strokeWidth={1} />);
    }
  }

  const active = members.filter((m) => m.lat != null);
  const cr = zoom >= 16 ? 30 : zoom >= 14 ? 50 : zoom >= 12 ? 70 : zoom >= 10 ? 90 : 120;
  const points = active.map((m) => {
    const p = toXY(m.lat, m.lng, cLat, cLng, zoom, w, h);
    return Object.assign({}, m, { _x: p.x, _y: p.y });
  });

  // 최대 줌(17+)에서는 클러스터 해제하고 오프셋으로 분리
  const clusters = [];
  const used = new Set();
  for (let i = 0; i < points.length; i++) {
    if (used.has(i)) continue;
    used.add(i);
    const g = [points[i]];
    for (let j = i + 1; j < points.length; j++) {
      if (used.has(j)) continue;
      if (Math.hypot(points[i]._x - points[j]._x, points[i]._y - points[j]._y) < cr) {
        g.push(points[j]);
        used.add(j);
      }
    }
    if (g.length === 1) {
      clusters.push({ type: "single", member: g[0], x: g[0]._x, y: g[0]._y });
    } else if (zoom >= 17) {
      // 최대 줌에서는 원형으로 분리
      const angleStep = (2 * Math.PI) / g.length;
      const spreadR = 28;
      g.forEach((m, idx) => {
        const angle = angleStep * idx - Math.PI / 2;
        clusters.push({ type: "single", member: m, x: m._x + Math.cos(angle) * spreadR, y: m._y + Math.sin(angle) * spreadR });
      });
    } else {
      const cx = g.reduce((a, m) => a + m._x, 0) / g.length;
      const cy = g.reduce((a, m) => a + m._y, 0) / g.length;
      const cc = {};
      g.forEach((m) => { cc[m.color] = (cc[m.color] || 0) + 1; });
      const dc = Object.entries(cc).sort((a, b) => b[1] - a[1])[0][0];
      clusters.push({ type: "cluster", members: g, x: cx, y: cy, color: dc, mixed: Object.keys(cc).length > 1, count: g.length });
    }
  }

  function handleSvgClick(e) {
    if (pinMode) {
      const r = e.currentTarget.getBoundingClientRect();
      onMapClick(e.clientX - r.left, e.clientY - r.top);
    }
  }

  return (
    <svg
      width={w}
      height={h}
      style={{ display: "block", userSelect: "none", touchAction: "none", cursor: pinMode ? "crosshair" : "grab", background: "transparent" }}
      onClick={handleSvgClick}
    >
      <rect width={w} height={h} fill="transparent" pointerEvents="all" />
      {lines}
      <line x1={w / 2 - 10} y1={h / 2} x2={w / 2 + 10} y2={h / 2} stroke={hideGrid ? "#00e5ff" : "#1e2433"} strokeWidth={hideGrid ? 1.5 : 1} opacity={hideGrid ? 0.6 : 1} />
      <line x1={w / 2} y1={h / 2 - 10} x2={w / 2} y2={h / 2 + 10} stroke={hideGrid ? "#00e5ff" : "#1e2433"} strokeWidth={hideGrid ? 1.5 : 1} opacity={hideGrid ? 0.6 : 1} />
      {pinMode && <rect width={w} height={h} fill="rgba(255,180,0,.03)" stroke="#ffd700" strokeWidth={2} strokeDasharray="8 6" />}

      {pins.map((pin) => {
        const xy = toXY(pin.lat, pin.lng, cLat, cLng, zoom, w, h);
        if (xy.x < -60 || xy.x > w + 60 || xy.y < -60 || xy.y > h + 60) return null;
        const pt = PIN_TYPES.find((t) => t.id === pin.type) || PIN_TYPES[3];
        const x = xy.x, y = xy.y;
        const pathD = "M" + x + "," + (y + 20) + " C" + (x - 14) + "," + (y + 5) + " " + (x - 14) + "," + (y - 20) + " " + x + "," + (y - 24) + " C" + (x + 14) + "," + (y - 20) + " " + (x + 14) + "," + (y + 5) + " " + x + "," + (y + 20) + "Z";
        return (
          <g key={pin.id} style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); onPinClick(pin); }}>
            <path d={pathD} fill={pt.color + "cc"} stroke={pt.color} strokeWidth={1.5} />
            <circle cx={x} cy={y - 8} r={9} fill="#0e1118" opacity={0.7} />
            <text x={x} y={y - 4} textAnchor="middle" fontSize={12} style={{ userSelect: "none" }}>{pt.icon}</text>
          </g>
        );
      })}

      {clusters.map((cl, idx) => {
        const x = cl.x, y = cl.y;
        if (x < -80 || x > w + 80 || y < -80 || y > h + 80) return null;
        if (cl.type === "single") {
          const m = cl.member;
          const sz = zoom >= 16 ? 56 : zoom >= 14 ? 46 : zoom >= 12 ? 36 : zoom >= 10 ? 26 : 18;
          const r = sz / 2;
          const c = m.color;
          const icon = m.role === "admin" ? "👑" : m.vehicle ? "🚔" : "👮‍♂️";
          const isMe = m.id === myId;
          return (
            <g key={m.id}>
              {m.online && (
                <circle cx={x} cy={y} r={r + 6} fill="none" stroke={c} strokeWidth={1.5} opacity={0.25}>
                  <animate attributeName="r" values={(r + 3) + ";" + (r + 16) + ";" + (r + 3)} dur="2.5s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.35;0;0.35" dur="2.5s" repeatCount="indefinite" />
                </circle>
              )}
              <circle cx={x} cy={y} r={r} fill={c + "22"} stroke={c} strokeWidth={isMe ? 3.5 : 2.5} />
              {zoom >= 11 ? (
                <text x={x} y={y + r * 0.38} textAnchor="middle" fontSize={sz * 0.75} style={{ userSelect: "none" }}>{icon}</text>
              ) : (
                <circle cx={x} cy={y} r={r / 3} fill={c} />
              )}
              {(zoom >= 13 || isMe) && (
                <g>
                  <rect x={x - (m.name.length * 4.5 + 10)} y={y - r - 26} width={m.name.length * 9 + 20} height={20} rx={10} fill={isMe ? c + "44" : "#1a1e28dd"} stroke={c + (isMe ? "cc" : "66")} strokeWidth={isMe ? 1.5 : 1} />
                  <text x={x} y={y - r - 12} textAnchor="middle" fill={isMe ? c : "#e8eaf0"} fontSize={11} fontWeight="700" style={{ userSelect: "none" }}>{m.name}{isMe ? " (나)" : ""}</text>
                </g>
              )}
            </g>
          );
        }
        const cR = (cl.count >= 10 ? 56 : cl.count >= 5 ? 48 : 40) / 2;
        const rc = cl.mixed ? "#00e5ff" : cl.color;
        return (
          <g key={"cl-" + idx}>
            <circle cx={x} cy={y} r={cR + 8} fill="none" stroke={rc} strokeWidth={1.5} opacity={0.3}>
              <animate attributeName="r" values={(cR + 4) + ";" + (cR + 18) + ";" + (cR + 4)} dur="2.5s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.4;0;0.4" dur="2.5s" repeatCount="indefinite" />
            </circle>
            <circle cx={x} cy={y} r={cR + 4} fill={rc + "15"} stroke={rc} strokeWidth={2} />
            <circle cx={x} cy={y} r={cR - 2} fill={cl.mixed ? "#0e1118" : cl.color + "33"} stroke={rc} strokeWidth={1.5} />
            <text x={x} y={y + cR * 0.18} textAnchor="middle" fill={rc} fontSize={cR * 0.95} fontWeight="900" fontFamily="monospace" style={{ userSelect: "none" }}>{cl.count}</text>
          </g>
        );
      })}

      <text x={w - 10} y={h - 10} textAnchor="end" fill="#1e2433" fontSize={9} fontFamily="monospace">
        {cLat.toFixed(4)}, {cLng.toFixed(4)}
      </text>
    </svg>
  );
}

function Btn({ children, onClick, v = "primary", style, disabled }) {
  const vs = {
    primary: { background: "#00e5ff", color: "#000", border: "none" },
    ghost: { background: "transparent", border: "1px solid " + G.border, color: G.dim },
    danger: { background: "rgba(255,68,68,.15)", color: "#ff4444", border: "1px solid rgba(255,68,68,.3)" },
    green: { background: "rgba(0,255,136,.13)", color: G.green, border: "1px solid rgba(0,255,136,.35)" },
    red: { background: "rgba(255,68,68,.13)", color: "#ff4444", border: "1px solid rgba(255,68,68,.35)" },
  };
  const baseStyle = {
    fontFamily: "inherit", fontWeight: 700, borderRadius: 12,
    cursor: disabled ? "default" : "pointer", transition: "all .15s",
    padding: "13px 18px", fontSize: 14, flex: 1,
    opacity: disabled ? 0.4 : 1,
  };
  return (
    <button disabled={disabled} onClick={onClick} style={Object.assign({}, baseStyle, vs[v], style || {})}>
      {children}
    </button>
  );
}

function Field(props) {
  const [f, setF] = useState(false);
  const { label } = props;
  const inputProps = Object.assign({}, props);
  delete inputProps.label;
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ fontSize: 11, letterSpacing: 1.5, color: G.dim, textTransform: "uppercase", marginBottom: 8, display: "block" }}>{label}</label>}
      <input
        {...inputProps}
        style={{ background: G.surface3, border: "1px solid " + (f ? G.accent : G.border), borderRadius: 12, color: G.text, fontFamily: "inherit", fontSize: 15, padding: "13px 16px", width: "100%", outline: "none", boxSizing: "border-box" }}
        onFocus={() => setF(true)}
        onBlur={() => setF(false)}
      />
    </div>
  );
}

function Sheet({ onClose, children }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.72)", backdropFilter: "blur(7px)", zIndex: 3000, display: "flex", alignItems: "flex-end" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: G.surface2, borderRadius: "24px 24px 0 0", padding: "12px 20px 36px", width: "100%", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 -20px 60px rgba(0,0,0,.6)" }}>
        <div style={{ width: 40, height: 4, background: G.border, borderRadius: 2, margin: "0 auto 22px" }} />
        {children}
      </div>
    </div>
  );
}

function ServerBadge({ status }) {
  const c = status === "connected" ? "#00ff88" : status === "connecting" ? "#ffd700" : "#ff4444";
  const l = status === "connected" ? "연결됨" : status === "connecting" ? "연결 중..." : "오프라인";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", background: "rgba(0,0,0,.4)", borderRadius: 20, border: "1px solid " + c + "44" }}>
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: c, boxShadow: "0 0 6px " + c }} />
      <span style={{ fontSize: 10, color: c, fontWeight: 700 }}>{l}</span>
    </div>
  );
}

function VehicleToggle({ val, onChange }) {
  return (
    <div onClick={onChange} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 14px", borderRadius: 12, cursor: "pointer", marginBottom: 16, background: val ? "rgba(255,107,53,.1)" : G.surface3, border: "1px solid " + (val ? "rgba(255,107,53,.5)" : G.border), transition: "all .15s" }}>
      <div style={{ fontSize: 24 }}>🚔</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: val ? "#ff6b35" : G.text }}>차량 운전자</div>
        <div style={{ fontSize: 11, color: G.dim, marginTop: 2 }}>지도에 차량 마크로 표시됩니다</div>
      </div>
      <div style={{ width: 22, height: 22, borderRadius: 6, border: "2px solid " + (val ? "#ff6b35" : "#374151"), background: val ? "#ff6b35" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "#fff" }}>
        {val && "✓"}
      </div>
    </div>
  );
}

function PinAddSheet({ lat, lng, onSave, onClose }) {
  const [type, setType] = useState("info");
  const [memo, setMemo] = useState("");
  return (
    <Sheet onClose={onClose}>
      <div style={{ fontFamily: "monospace", fontSize: 20, fontWeight: 900, color: "#ffd700", letterSpacing: 2, marginBottom: 4 }}>📌 핀 추가</div>
      <div style={{ fontSize: 12, color: G.dim, marginBottom: 20 }}>{lat.toFixed(5)}, {lng.toFixed(5)}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
        {PIN_TYPES.map((t) => (
          <div key={t.id} onClick={() => setType(t.id)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 20, cursor: "pointer", fontSize: 13, fontWeight: 700, background: type === t.id ? t.color + "25" : G.surface3, border: "1px solid " + (type === t.id ? t.color : G.border), color: type === t.id ? t.color : G.dim }}>
            {t.icon} {t.label}
          </div>
        ))}
      </div>
      <Field label="메모 (선택)" placeholder="현장 상황..." value={memo} onChange={(e) => setMemo(e.target.value)} />
      <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
        <Btn v="ghost" onClick={onClose}>취소</Btn>
        <Btn onClick={() => onSave({ type: type, memo: memo })}>📌 추가</Btn>
      </div>
    </Sheet>
  );
}

function PinDetailSheet({ pin, onDelete, onClose }) {
  const pt = PIN_TYPES.find((t) => t.id === pin.type) || PIN_TYPES[3];
  return (
    <Sheet onClose={onClose}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: pt.color + "18", border: "2px solid " + pt.color + "55", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>{pt.icon}</div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 900, color: pt.color }}>{pt.label}</div>
          <div style={{ fontSize: 12, color: G.dim, marginTop: 3 }}>👮 {pin.author}</div>
        </div>
      </div>
      {pin.memo && <div style={{ background: G.surface3, borderRadius: 12, padding: "12px 14px", fontSize: 14, color: G.text, marginBottom: 16 }}>{pin.memo}</div>}
      <div style={{ fontSize: 12, color: G.dim, marginBottom: 18 }}>📍 {pin.lat.toFixed(5)}, {pin.lng.toFixed(5)}</div>
      <div style={{ display: "flex", gap: 10 }}>
        <Btn v="ghost" onClick={onClose}>닫기</Btn>
        <Btn v="danger" onClick={() => onDelete(pin.id)}>🗑 삭제</Btn>
      </div>
    </Sheet>
  );
}

function SendMsgSheet({ cLat, cLng, onSend, onClose }) {
  const [text, setText] = useState("");
  const [loc, setLoc] = useState(null);
  const [customLat, setCustomLat] = useState(null);
  const [customLng, setCustomLng] = useState(null);
  const [pickMode, setPickMode] = useState(false);
  const [miniZoom, setMiniZoom] = useState(14);
  const [miniCLat, setMiniCLat] = useState(cLat);
  const [miniCLng, setMiniCLng] = useState(cLng);
  const [mapSize, setMapSize] = useState({ w: 300, h: 200 });
  const mapContainerRef = useRef(null);
  const drag = useRef(null);
  const pinch = useRef(null);
  const didDrag = useRef(false);

  useEffect(() => {
    if (mapContainerRef.current) {
      setMapSize({ w: mapContainerRef.current.clientWidth, h: 200 });
    }
  }, [pickMode, loc]);

  function handleSend() {
    let sendLat = null, sendLng = null;
    if (loc === "current") { sendLat = cLat; sendLng = cLng; }
    if (loc === "custom" && customLat != null) { sendLat = customLat; sendLng = customLng; }
    onSend({ text: text.trim(), lat: sendLat, lng: sendLng });
    onClose();
  }

  function handleMapClick(e) {
    // 드래그 후엔 클릭 무시
    if (didDrag.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const picked = fromXY(px, py, miniCLat, miniCLng, miniZoom, mapSize.w, mapSize.h);
    setCustomLat(picked.lat);
    setCustomLng(picked.lng);
    setLoc("custom");
    setPickMode(false);
  }

  function onMapMouseDown(e) {
    drag.current = { x: e.clientX, y: e.clientY, lat: miniCLat, lng: miniCLng };
    didDrag.current = false;
  }
  function onMapMouseMove(e) {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x, dy = e.clientY - drag.current.y;
    if (Math.hypot(dx, dy) > 3) didDrag.current = true;
    const z = Math.round(miniZoom);
    setMiniCLat(worldYToLat(latToWorldY(drag.current.lat, z) - dy, z));
    setMiniCLng(worldXToLng(lngToWorldX(drag.current.lng, z) - dx, z));
  }
  function onMapTouchStart(e) {
    didDrag.current = false;
    if (e.touches.length === 1) {
      drag.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, lat: miniCLat, lng: miniCLng };
      pinch.current = null;
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinch.current = { dist: Math.hypot(dx, dy), zoom: miniZoom };
      drag.current = null;
    }
  }
  function onMapTouchMove(e) {
    e.preventDefault();
    didDrag.current = true;
    if (e.touches.length === 1 && drag.current) {
      const dx = e.touches[0].clientX - drag.current.x, dy = e.touches[0].clientY - drag.current.y;
      const z = Math.round(miniZoom);
      setMiniCLat(worldYToLat(latToWorldY(drag.current.lat, z) - dy, z));
      setMiniCLng(worldXToLng(lngToWorldX(drag.current.lng, z) - dx, z));
    } else if (e.touches.length === 2 && pinch.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      setMiniZoom(Math.max(3, Math.min(18, pinch.current.zoom + Math.log2(Math.hypot(dx, dy) / pinch.current.dist))));
    }
  }
  function onMapTouchEnd(e) {
    if (e.touches.length === 0 && !didDrag.current && e.changedTouches.length === 1) {
      const rect = mapContainerRef.current.getBoundingClientRect();
      const px = e.changedTouches[0].clientX - rect.left;
      const py = e.changedTouches[0].clientY - rect.top;
      const picked = fromXY(px, py, miniCLat, miniCLng, miniZoom, mapSize.w, mapSize.h);
      setCustomLat(picked.lat);
      setCustomLng(picked.lng);
      setLoc("custom");
      setPickMode(false);
    }
    drag.current = null;
    pinch.current = null;
  }

  const showMiniMap = pickMode || loc === "custom";

  return (
    <Sheet onClose={onClose}>
      <div style={{ fontFamily: "monospace", fontSize: 20, fontWeight: 900, color: G.accent, letterSpacing: 2, marginBottom: 18 }}>📢 메시지 전송</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        {QUICK_MSGS.map((q) => (
          <div key={q} onClick={() => setText(q)} style={{ padding: "7px 14px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 700, background: text === q ? G.accent + "25" : G.surface3, border: "1px solid " + (text === q ? G.accent : G.border), color: text === q ? G.accent : G.dim }}>{q}</div>
        ))}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, 100))}
        placeholder="직접 입력..."
        style={{ background: G.surface3, border: "1px solid " + G.border, borderRadius: 12, color: G.text, fontFamily: "inherit", fontSize: 14, padding: "12px 14px", width: "100%", outline: "none", boxSizing: "border-box", resize: "none", height: 80, marginBottom: 16 }}
      />

      <label style={{ fontSize: 11, letterSpacing: 1.5, color: G.dim, textTransform: "uppercase", marginBottom: 8, display: "block" }}>위치 첨부</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {[{ id: null, label: "없음" }, { id: "current", label: "📍 현재 위치" }, { id: "pick", label: "🗺️ 직접 선택" }].map((o) => (
          <div key={String(o.id)} onClick={() => {
            if (o.id === "pick") {
              setPickMode(true);
              setLoc(null);
              setMiniCLat(customLat || cLat);
              setMiniCLng(customLng || cLng);
            } else {
              setLoc(o.id);
              setPickMode(false);
            }
          }} style={{ flex: 1, padding: "9px 4px", borderRadius: 12, cursor: "pointer", textAlign: "center", fontSize: 11, fontWeight: 700, background: (loc === o.id || (o.id === "pick" && pickMode)) ? G.accent + "15" : G.surface3, border: "1px solid " + ((loc === o.id || (o.id === "pick" && pickMode)) ? G.accent : G.border), color: (loc === o.id || (o.id === "pick" && pickMode)) ? G.accent : G.dim }}>{o.label}</div>
        ))}
      </div>

      {showMiniMap && (
        <div style={{ marginBottom: 12 }}>
          <div
            ref={mapContainerRef}
            style={{ position: "relative", width: "100%", height: 200, borderRadius: 14, overflow: "hidden", border: "1px solid " + (pickMode ? G.accent : G.border), cursor: "crosshair", touchAction: "none" }}
            onClick={handleMapClick}
            onMouseDown={onMapMouseDown}
            onMouseMove={onMapMouseMove}
            onMouseUp={() => { drag.current = null; }}
            onMouseLeave={() => { drag.current = null; }}
            onTouchStart={onMapTouchStart}
            onTouchMove={onMapTouchMove}
            onTouchEnd={onMapTouchEnd}
          >
            <NaverMap cLat={miniCLat} cLng={miniCLng} zoom={miniZoom} onReady={() => {}} onError={() => {}} />
            <div style={{ position: "absolute", inset: 0, zIndex: 2 }}>
              <MapSVG
                members={[]}
                pins={customLat != null ? [{ id: "sel", lat: customLat, lng: customLng, type: "info", author: "" }] : []}
                zoom={Math.round(miniZoom)} cLat={miniCLat} cLng={miniCLng} w={mapSize.w} h={200}
                onMapClick={() => {}} onPinClick={() => {}} pinMode={false} hideGrid={true} myId=""
              />
            </div>
            <div style={{ position: "absolute", top: 8, left: 0, right: 0, textAlign: "center", zIndex: 10, pointerEvents: "none" }}>
              <span style={{ background: "rgba(0,0,0,.75)", color: loc === "custom" ? G.green : "#ffd700", fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20 }}>
                {loc === "custom" ? "📍 탭해서 위치 변경" : "탭해서 위치 선택"}
              </span>
            </div>
            {customLat != null && (
              <div style={{ position: "absolute", bottom: 42, left: 8, zIndex: 10, pointerEvents: "none" }}>
                <span style={{ background: "rgba(0,0,0,.75)", color: G.green, fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 12 }}>📍 {customLat.toFixed(4)}, {customLng.toFixed(4)}</span>
              </div>
            )}
            <div style={{ position: "absolute", right: 8, bottom: 8, zIndex: 10, display: "flex", flexDirection: "column", gap: 4 }}>
              {[{ i: "＋", fn: () => setMiniZoom((z) => Math.min(18, z + 1)) }, { i: "－", fn: () => setMiniZoom((z) => Math.max(3, z - 1)) }].map((b) => (
                <div key={b.i} onClick={(e) => { e.stopPropagation(); b.fn(); }} style={{ width: 30, height: 30, background: "rgba(0,0,0,.7)", border: "1px solid " + G.border, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, cursor: "pointer", color: G.text }}>
                  {b.i}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {loc === "current" && (
        <div style={{ fontSize: 11, color: G.green, marginBottom: 12 }}>📍 현재 위치: {cLat.toFixed(4)}, {cLng.toFixed(4)}</div>
      )}

      <div style={{ display: "flex", gap: 10 }}>
        <Btn v="ghost" onClick={onClose}>취소</Btn>
        <Btn disabled={!text.trim()} onClick={handleSend}>📢 전송</Btn>
      </div>
    </Sheet>
  );
}

function MsgPopup({ msg, index, color, onDismiss, onFlyTo }) {
  return (
    <div style={{ position: "fixed", top: 80 + index * 116, left: 16, right: 16, zIndex: 2000, background: G.surface2, border: "2px solid " + color, borderRadius: 16, padding: "14px 16px", boxShadow: "0 8px 32px rgba(0,0,0,.6)" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: color + "22", border: "1.5px solid " + color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>📢</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: color }}>{msg.from}</span>
            <span style={{ fontSize: 10, color: G.dim }}>{new Date(msg.at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}</span>
          </div>
          <div style={{ fontSize: 14, color: G.text, lineHeight: 1.5 }}>{msg.text}</div>
          {msg.lat != null && (
            <div onClick={() => onFlyTo(msg.lat, msg.lng)} style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 8, background: "rgba(0,229,255,.1)", border: "1px solid rgba(0,229,255,.3)", borderRadius: 20, padding: "5px 12px", cursor: "pointer", fontSize: 12, color: G.accent, fontWeight: 700 }}>📍 위치 보기</div>
          )}
        </div>
        <div onClick={onDismiss} style={{ color: G.dim, fontSize: 20, cursor: "pointer", lineHeight: 1, flexShrink: 0 }}>✕</div>
      </div>
    </div>
  );
}

function MemberPanel(props) {
  const { members, pins, messages, myInfo, open, onToggle, sharing, onShare, onStop, onFocus, onLeaveRequest, onToggleVehicle, onFocusLocation, onFocusMsgLocation, secureMode, onToggleSecureMode, serverStatus, onInvite } = props;
  const [tab, setTab] = useState("members");
  const online = members.filter((m) => m.online).length;
  const headerLabel = tab === "members" ? "CREW(" + members.length + "명)" : tab === "pins" ? "PINS (" + pins.length + "개)" : "메세지";

  return (
    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 20, background: G.surface, borderTop: "1px solid " + G.border, borderRadius: "20px 20px 0 0", height: open ? "68%" : 72, transition: "height .3s cubic-bezier(.16,1,.3,1)", display: "flex", flexDirection: "column", boxShadow: "0 -8px 40px rgba(0,0,0,.5)", overflow: "hidden" }}>
      <div onClick={onToggle} style={{ padding: "12px 0 0", cursor: "pointer", flexShrink: 0 }}>
        <div style={{ width: 36, height: 4, background: G.border, borderRadius: 2, margin: "0 auto 10px" }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 18px 10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontFamily: "monospace", fontWeight: 900, fontSize: 13, color: G.accent, letterSpacing: 2 }}>{headerLabel}</div>
            {tab === "members" && (
              <div onClick={(e) => { e.stopPropagation(); onInvite(); }} style={{ padding: "3px 10px", borderRadius: 20, background: "rgba(0,229,255,.12)", border: "1px solid rgba(0,229,255,.35)", fontSize: 11, fontWeight: 700, color: G.accent, cursor: "pointer" }}>초대</div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ServerBadge status={serverStatus} />
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: online > 0 ? G.green : "#374151" }} />
            <span style={{ fontSize: 12, color: G.dim }}>{online}명 온라인</span>
            <span style={{ fontSize: 16, color: G.dim }}>{open ? "▼" : "▲"}</span>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", margin: "0 14px 10px", background: G.surface3, borderRadius: 12, padding: 3, flexShrink: 0 }}>
        {[{ id: "members", label: "👮 경찰관" }, { id: "pins", label: "📌 핀" }, { id: "msgs", label: "💬 메시지" }].map((t) => (
          <div key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer", background: tab === t.id ? G.surface2 : "transparent", color: tab === t.id ? G.accent : G.dim, border: "1px solid " + (tab === t.id ? G.border : "transparent") }}>{t.label}</div>
        ))}
      </div>

      {tab === "members" && (
        <div style={{ margin: "0 14px 10px", background: G.surface3, borderRadius: 14, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <div style={{ width: 9, height: 9, borderRadius: "50%", background: sharing ? G.green : "#374151", flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: myInfo.color }}>
              {myInfo.name} {myInfo.role === "admin" && "👑"}
            </div>
            <div style={{ fontSize: 11, color: G.dim, marginTop: 2 }}>{sharing ? "위치 공유 중" : "공유 꺼짐"}</div>
          </div>
          <div onClick={onToggleVehicle} style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 10, background: myInfo.vehicle ? "rgba(255,107,53,.15)" : G.surface2, border: "1px solid " + (myInfo.vehicle ? "#ff6b35" : G.border), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, cursor: "pointer" }}>
            {myInfo.vehicle ? "🚔" : "👮"}
          </div>
          <Btn v={sharing ? "red" : "green"} onClick={sharing ? onStop : onShare} style={{ flex: "unset", padding: "9px 16px", fontSize: 13, borderRadius: 10 }}>
            {sharing ? "⏹ 중단" : "▶ 공유"}
          </Btn>
        </div>
      )}

      <div style={{ flex: 1, overflowY: "auto", padding: "0 14px" }}>
        {tab === "members" && members.map((m) => {
          const icon = m.role === "admin" ? "👑" : m.vehicle ? "🚔" : "👮‍♂️";
          const locText = m.lat ? m.lat.toFixed(4) + ", " + m.lng.toFixed(4) : "위치 없음";
          return (
            <div key={m.id} onClick={() => onFocus(m)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid " + G.border + "55", cursor: "pointer" }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: m.color + "18", border: "2px solid " + (m.online ? m.color : "#252a38"), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{icon}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: m.id === myInfo.id ? m.color : G.text }}>
                  {m.name}
                  {m.id === myInfo.id && <span style={{ color: G.dim, fontSize: 10 }}> (나)</span>}
                  {m.team && <span style={{ marginLeft: 6, fontSize: 9, padding: "1px 6px", borderRadius: 8, background: m.color + "22", color: m.color, fontWeight: 700 }}>{m.team}</span>}
                </div>
                <div style={{ fontSize: 10, color: G.dim, marginTop: 2 }}>{locText}</div>
              </div>
              <div style={{ fontSize: 10, padding: "3px 8px", borderRadius: 20, background: m.online ? "rgba(0,255,136,.08)" : "rgba(75,85,99,.1)", color: m.online ? G.green : G.dim }}>
                {m.online ? "● ON" : "○"}
              </div>
            </div>
          );
        })}

        {tab === "pins" && pins.length === 0 && (
          <div style={{ textAlign: "center", color: "#2a3040", fontSize: 13, padding: "30px 0", lineHeight: 2 }}>📌<br />핀이 없습니다</div>
        )}
        {tab === "pins" && pins.length > 0 && pins.map((pin) => {
          const pt = PIN_TYPES.find((t) => t.id === pin.type) || PIN_TYPES[3];
          return (
            <div key={pin.id} onClick={() => onFocusLocation(pin.lat, pin.lng)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid " + G.border + "55", cursor: "pointer" }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: pt.color + "18", border: "1.5px solid " + pt.color + "55", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{pt.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: pt.color }}>{pt.label}</div>
                <div style={{ fontSize: 11, color: G.dim, marginTop: 2 }}>{pin.memo || "메모 없음"}</div>
              </div>
            </div>
          );
        })}

        {tab === "msgs" && messages.length === 0 && (
          <div style={{ textAlign: "center", color: "#2a3040", fontSize: 13, padding: "30px 0", lineHeight: 2 }}>💬<br />메시지가 없습니다</div>
        )}
        {tab === "msgs" && messages.length > 0 && messages.slice().reverse().map((msg) => {
          const c = msg.color || G.accent;
          return (
            <div key={msg.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 0", borderBottom: "1px solid " + G.border + "55" }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: c + "22", border: "1.5px solid " + c + "55", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>👮‍♂️</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: c }}>{msg.from}</span>
                  <span style={{ fontSize: 10, color: G.dim }}>{new Date(msg.at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <div style={{ fontSize: 13, color: G.text, lineHeight: 1.5 }}>{msg.text}</div>
                {msg.lat != null && (
                  <div onClick={() => onFocusMsgLocation(msg.lat, msg.lng)} style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 8, background: "rgba(0,229,255,.1)", border: "1px solid rgba(0,229,255,.3)", borderRadius: 20, padding: "5px 12px", cursor: "pointer", fontSize: 12, color: G.accent, fontWeight: 700 }}>📍 위치 보기</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {myInfo.role === "admin" && (
        <div onClick={onToggleSecureMode} style={{ padding: "10px 14px", borderTop: "1px solid " + G.border, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", background: secureMode ? "rgba(167,139,250,.08)" : "transparent" }}>
          <div style={{ fontSize: 18 }}>{secureMode ? "🔒" : "🔓"}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: secureMode ? "#a78bfa" : G.text }}>보안 강화 모드</div>
            <div style={{ fontSize: 10, color: G.dim }}>{secureMode ? "ON · 방장만 위치 확인" : "OFF · 모두 서로 위치 확인"}</div>
          </div>
          <div style={{ width: 36, height: 20, borderRadius: 10, background: secureMode ? "#a78bfa" : G.border, position: "relative" }}>
            <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: secureMode ? 18 : 2, transition: "left .2s" }} />
          </div>
        </div>
      )}

      <div style={{ padding: "12px 14px", display: "flex", gap: 8, flexShrink: 0, borderTop: "1px solid " + G.border }}>
        <Btn v="danger" onClick={onLeaveRequest} style={{ fontSize: 12 }}>나가기</Btn>
      </div>
    </div>
  );
}

function RoomScreen({ roomData, myInfo, setMyInfo, onLeave, showToast }) {
  const [members, setMembers] = useState(Object.values(roomData.members || {}));
  const [pins, setPins] = useState([]);
  const [messages, setMessages] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [zoom, setZoom] = useState(13);
  const [cLat, setCLat] = useState(ULSAN.lat);
  const [cLng, setCLng] = useState(ULSAN.lng);
  const [csz, setCsz] = useState({ w: 390, h: 600 });
  const [panelOpen, setPanelOpen] = useState(false);
  const [pinMode, setPinMode] = useState(false);
  const [pendingPin, setPendingPin] = useState(null);
  const [selPin, setSelPin] = useState(null);
  const [popups, setPopups] = useState([]);
  const [showSend, setShowSend] = useState(false);
  const [showLeave, setShowLeave] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [focusedMsgPin, setFocusedMsgPin] = useState(null); // 메시지 위치 임시 마커
  const [secureMode, setSecureMode] = useState(false);
  const secureModeRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [serverStatus, setServerStatus] = useState("connecting");

  const mapRef = useRef(null);
  const watchRef = useRef(null);
  const drag = useRef(null);
  const pinch = useRef(null);
  const longTimer = useRef(null);
  const didDrag = useRef(false);
  const myInfoRef = useRef(myInfo);
  useEffect(() => { myInfoRef.current = myInfo; }, [myInfo]);

  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setCsz({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el);
    setCsz({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  // 방에서 나갈 때 Foreground Service 강제 중단
  useEffect(() => {
    return () => {
      try {
        if (watchRef.current) {
          navigator.geolocation.clearWatch(watchRef.current);
          watchRef.current = null;
        }
        if (window.AndroidBridge) {
          window.AndroidBridge.stopForegroundService();
        }
      } catch (e) {}
    };
  }, []);

  const send = useWS(roomData.code, myInfo.id, myInfo, function (msg) {
    setServerStatus("connected");
    if (msg.type === "members") {
      const memberList = Object.values(msg.members || {});
      setMembers(memberList);
      // 서버에서 내 role이 변경됐으면 myInfo 업데이트 (방장 복원 등)
      const myUpdated = (msg.members || {})[myInfo.id];
      if (myUpdated && myUpdated.role !== myInfo.role) {
        if (setMyInfo) setMyInfo((prev) => ({ ...prev, role: myUpdated.role }));
        else Object.assign(myInfo, { role: myUpdated.role });
      }
    }
    if (msg.type === "pins") setPins(msg.pins || []);
    if (msg.type === "secureMode") {
      setSecureMode(msg.secureMode);
      secureModeRef.current = msg.secureMode;
      showToast(msg.secureMode ? "🔒 보안 모드 ON" : "🔓 보안 모드 OFF");
    }
    if (msg.type === "chat") {
      const m = msg.message;
      setMessages((prev) => {
        if (prev.find((x) => x.id === m.id)) return prev;
        if (m.from !== myInfoRef.current.name) setPopups((p) => [...p, m]);
        return prev.concat([m]);
      });
    }
    if (msg.type === "leave_ack") {
      // 서버가 나가기를 확인함 - 이미 onLeave 예약됨
    }
    if (msg.type === "room_closed") {
      showToast("🚪 방장이 방을 닫았습니다");
      setTimeout(() => onLeave(), 1500);
    }
  });

  useEffect(() => {
    const check = async () => {
      try {
        const r = await fetch(SERVER_URL + "/");
        setServerStatus(r.ok ? "connected" : "error");
      } catch (e) {
        setServerStatus("error");
      }
    };
    check();
    const id = setInterval(check, 10000);
    return () => clearInterval(id);
  }, []);

  // 지도 마커용 - 보안모드에서 본인만 표시
  const visibleMembers = (secureMode && myInfo.role !== "admin")
    ? members.filter((m) => m.id === myInfo.id)
    : members;
  // Foreground Service 시작 (Android 전용)
  async function startForegroundService() {
    try {
      const { CapacitorCustomPlatform } = await import("@capacitor/core");
      if (CapacitorCustomPlatform) return; // 웹 환경 무시
    } catch (e) {}
    try {
      const { Capacitor } = await import("@capacitor/core");
      if (Capacitor.getPlatform() !== "android") return;
      // Android Intent로 Foreground Service 시작
      const { App } = await import("@capacitor/app");
      await fetch("capacitor://localhost/LocationForegroundService/start").catch(() => {});
    } catch (e) {}
    // Android native bridge 직접 호출
    try {
      if (window.AndroidBridge) {
        window.AndroidBridge.startForegroundService();
      }
    } catch (e) {}
  }

  async function stopForegroundService() {
    try {
      if (window.AndroidBridge) {
        window.AndroidBridge.stopForegroundService();
      }
    } catch (e) {}
  }

  function startShare() {
    if (!navigator.geolocation) {
      showToast("❌ 위치 API 미지원");
      return;
    }
    // 권한 반영 대기 후 위치 추적 + Foreground Service 시작 (크래시 방지)
    setTimeout(() => {
      startForegroundService();
      let firstLocation = true;
      watchRef.current = navigator.geolocation.watchPosition(
        function (pos) {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          send({ type: "location", code: roomData.code, id: myInfo.id, lat: lat, lng: lng });
          setMembers((prev) => prev.map((m) => m.id === myInfo.id ? Object.assign({}, m, { lat: lat, lng: lng, online: true }) : m));
          if (firstLocation) {
            setCLat(lat);
            setCLng(lng);
            setZoom(15);
            firstLocation = false;
          }
          setSharing(true);
        },
        function (err) { if (err.code === 1) showToast("⚠ 위치 권한이 거부됐습니다"); },
        { enableHighAccuracy: true, maximumAge: 4000, timeout: 10000 }
      );
    }, 1500);
  }

  function stopShare() {
    if (watchRef.current) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
    stopForegroundService();
    setSharing(false);
    showToast("📍 공유 중단");
  }

  function savePin(opts) {
    if (!pendingPin) return;
    const pin = Object.assign({ id: genId() }, pendingPin, { type: opts.type, memo: opts.memo, author: myInfo.name, createdAt: Date.now() });
    send({ type: "pin", code: roomData.code, pin: pin });
    setPins((ps) => ps.concat([pin]));
    setPendingPin(null);
    showToast("📌 핀 추가됨");
  }

  function deletePin(id) {
    setPins((ps) => ps.filter((p) => p.id !== id));
    setSelPin(null);
    showToast("🗑 핀 삭제됨");
  }

  function sendMsg(opts) {
    const msg = { id: genId(), from: myInfo.name, color: myInfo.color, text: opts.text, lat: opts.lat, lng: opts.lng, at: Date.now() };
    send({ type: "chat", code: roomData.code, message: msg });
    setMessages((prev) => prev.concat([msg]));
    setPopups((p) => p.concat([Object.assign({}, msg, { self: true })]));
    // 서버에 FCM 푸시 알림 전송 요청
    fetch(SERVER_URL + "/notify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code: roomData.code,
        senderId: myInfo.id,
        from: myInfo.name,
        text: opts.text,
      }),
    }).catch(() => {});
  }

  function flyTo(lat, lng, isMsg = false) {
    setCLat(lat);
    setCLng(lng);
    setZoom(16);
    if (isMsg) {
      setFocusedMsgPin({ id: "msg-pin", lat, lng, type: "alert", author: "메시지" });
      setTimeout(() => setFocusedMsgPin(null), 5000);
    }
  }

  function focusMember(m) {
    if (m.lat) flyTo(m.lat, m.lng);
    else showToast("위치 없음");
  }

  function toggleVehicle() {
    const newVehicle = !myInfo.vehicle;
    if (setMyInfo) setMyInfo((prev) => ({ ...prev, vehicle: newVehicle }));
    else myInfo.vehicle = newVehicle;
    setMembers((prev) => prev.map((m) => m.id === myInfo.id ? Object.assign({}, m, { vehicle: newVehicle }) : m));
    send({ type: "updateInfo", code: roomData.code, id: myInfo.id, info: { vehicle: newVehicle } });
    showToast(newVehicle ? "🚔 차량 모드" : "👮 도보 모드");
  }

  function onMouseDown(e) {
    drag.current = { x: e.clientX, y: e.clientY, lat: cLat, lng: cLng };
    didDrag.current = false;
  }

  function onMouseMove(e) {
    if (!drag.current) return;
    didDrag.current = true;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    const z = Math.round(zoom);
    setCLat(worldYToLat(latToWorldY(drag.current.lat, z) - dy, z));
    setCLng(worldXToLng(lngToWorldX(drag.current.lng, z) - dx, z));
  }

  function onWheel(e) {
    e.preventDefault();
    setZoom((z) => Math.max(3, Math.min(18, z + (e.deltaY < 0 ? 1 : -1))));
  }

  function onTouchStart(e) {
    didDrag.current = false;
    if (e.touches.length === 1) {
      drag.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, lat: cLat, lng: cLng };
      pinch.current = null;
      longTimer.current = setTimeout(function () {
        if (!didDrag.current) {
          setPinMode(true);
          showToast("📌 길게 눌러 핀 추가");
        }
      }, 600);
    } else if (e.touches.length === 2) {
      clearTimeout(longTimer.current);
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinch.current = { dist: Math.hypot(dx, dy), zoom: zoom };
      drag.current = null;
    }
  }

  function onTouchMove(e) {
    e.preventDefault();
    didDrag.current = true;
    clearTimeout(longTimer.current);
    if (e.touches.length === 1 && drag.current) {
      const dx = e.touches[0].clientX - drag.current.x;
      const dy = e.touches[0].clientY - drag.current.y;
      const z = Math.round(zoom);
      setCLat(worldYToLat(latToWorldY(drag.current.lat, z) - dy, z));
      setCLng(worldXToLng(lngToWorldX(drag.current.lng, z) - dx, z));
    } else if (e.touches.length === 2 && pinch.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      setZoom(Math.max(3, Math.min(18, pinch.current.zoom + Math.log2(Math.hypot(dx, dy) / pinch.current.dist))));
    }
  }

  function onTouchEnd() {
    drag.current = null;
    pinch.current = null;
    clearTimeout(longTimer.current);
  }

  function handleMapClick(px, py) {
    setPendingPin(fromXY(px, py, cLat, cLng, zoom, csz.w, csz.h));
    setPinMode(false);
  }

  const ctrlBtns = [
    { i: "📢", fn: () => setShowSend(true) },
    { i: "📌", fn: function () { setPinMode((p) => !p); if (!pinMode) showToast("📌 지도 클릭으로 핀 추가"); }, active: pinMode },
    { i: "⌖", fn: function () { const m = members.find((x) => x.id === myInfo.id); if (m && m.lat) { setCLat(m.lat); setCLng(m.lng); setZoom(16); } else showToast("위치 없음"); } },
    { i: "🗺️", fn: function () { const ps = members.filter((m) => m.lat); if (!ps.length) return; setCLat(ps.reduce((a, m) => a + m.lat, 0) / ps.length); setCLng(ps.reduce((a, m) => a + m.lng, 0) / ps.length); setZoom(13); } },
    { i: "＋", fn: () => setZoom((z) => Math.min(18, z + 1)) },
    { i: "－", fn: () => setZoom((z) => Math.max(3, z - 1)) },
  ];

  const onlineCount = members.filter((m) => m.online).length;

  return (
    <div style={{ flex: 1, position: "relative", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <div
        ref={mapRef}
        style={{ flex: 1, position: "relative", overflow: "hidden", touchAction: "none", background: "#0d1420" }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={() => { drag.current = null; }}
        onMouseLeave={() => { drag.current = null; }}
        onWheel={onWheel}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <NaverMap cLat={cLat} cLng={cLng} zoom={zoom} onReady={() => setMapReady(true)} onError={() => setMapError(true)} />

        <div style={{ position: "absolute", inset: 0, zIndex: 2, pointerEvents: "auto", background: "transparent" }}>
          <MapSVG
            members={visibleMembers}
            pins={focusedMsgPin ? [...pins, focusedMsgPin] : pins}
            zoom={Math.round(zoom)}
            cLat={cLat}
            cLng={cLng}
            w={csz.w}
            h={csz.h}
            onMapClick={handleMapClick}
            onPinClick={setSelPin}
            pinMode={pinMode}
            hideGrid={mapReady}
            myId={myInfo.id}
          />
        </div>

        {!mapReady && !mapError && (
          <div style={{ position: "absolute", bottom: 88, left: "50%", transform: "translateX(-50%)", zIndex: 5, background: "#12151cdd", border: "1px solid " + G.border, borderRadius: 20, padding: "6px 14px", fontSize: 11, color: G.dim, pointerEvents: "none" }}>
            🗺️ 지도 로딩 중...
          </div>
        )}

        <div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 400, background: "linear-gradient(to bottom,rgba(8,11,17,.92) 60%,transparent)", padding: "14px 16px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontFamily: "monospace", fontWeight: 900, fontSize: 16, color: G.accent, letterSpacing: 3 }}>{roomData.name}</div>
            <div style={{ fontSize: 11, color: G.dim, marginTop: 2 }}>📡 {roomData.code} · {onlineCount}/{members.length}명</div>
          </div>
          <div onClick={sharing ? stopShare : startShare} style={{ display: "flex", alignItems: "center", gap: 8, background: sharing ? "rgba(0,255,136,.1)" : "rgba(0,229,255,.1)", border: "1px solid " + (sharing ? "rgba(0,255,136,.4)" : "rgba(0,229,255,.3)"), borderRadius: 20, padding: "8px 14px", cursor: "pointer" }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: sharing ? G.green : G.accent, boxShadow: "0 0 6px " + (sharing ? G.green : G.accent) }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: sharing ? G.green : G.accent }}>{sharing ? "공유중" : "공유시작"}</span>
          </div>
        </div>

        {pinMode && (
          <div style={{ position: "absolute", top: 68, left: 16, right: 16, zIndex: 400, background: "rgba(255,215,0,.12)", border: "1px solid rgba(255,215,0,.5)", borderRadius: 12, padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 13, color: "#ffd700", fontWeight: 700 }}>📌 지도를 탭해서 핀을 추가하세요</span>
            <span onClick={() => setPinMode(false)} style={{ fontSize: 13, color: G.dim, cursor: "pointer" }}>✕</span>
          </div>
        )}

        <div style={{ position: "absolute", bottom: panelOpen ? "calc(68% + 16px)" : 88, left: 14, zIndex: 300, background: "#12151cdd", border: "1px solid " + G.border, borderRadius: 10, padding: "6px 12px", fontSize: 10, color: G.dim, pointerEvents: "none" }}>
          Z<span style={{ color: G.accent, fontWeight: 900, fontSize: 17 }}>{Math.round(zoom)}</span>
        </div>

        <div style={{ position: "absolute", bottom: panelOpen ? "calc(68% + 16px)" : 88, right: 14, zIndex: 300, display: "flex", flexDirection: "column", gap: 8 }}>
          {ctrlBtns.map((b, i) => (
            <div key={i} onClick={b.fn} style={{ width: 44, height: 44, background: b.active ? "rgba(255,215,0,.15)" : "#12151cdd", border: "1px solid " + (b.active ? "#ffd700" : G.border), borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, cursor: "pointer" }}>
              {b.i}
            </div>
          ))}
        </div>
      </div>

      <MemberPanel
        members={members}
        pins={pins}
        messages={messages}
        myInfo={myInfo}
        open={panelOpen}
        onToggle={() => setPanelOpen((p) => !p)}
        sharing={sharing}
        onShare={startShare}
        onStop={stopShare}
        onFocus={(m) => { focusMember(m); setPanelOpen(false); }}
        onLeaveRequest={() => setShowLeave(true)}
        onToggleVehicle={toggleVehicle}
        onFocusLocation={(lat, lng) => { flyTo(lat, lng); setPanelOpen(false); }}
        onFocusMsgLocation={(lat, lng) => { flyTo(lat, lng, true); setPanelOpen(false); }}
        secureMode={secureMode}
        onToggleSecureMode={() => {
          const n = !secureMode;
          setSecureMode(n);
          secureModeRef.current = n;
          send({ type: "secureMode", code: roomData.code, secureMode: n });
          showToast(n ? "🔒 보안 모드 ON" : "🔓 보안 모드 OFF");
        }}
        serverStatus={serverStatus}
        onInvite={() => setShowInvite(true)}
      />

      {popups.map((msg, idx) => (
        <MsgPopup
          key={msg.id}
          msg={msg}
          index={idx}
          color={msg.color || G.accent}
          onDismiss={() => setPopups((p) => p.filter((m) => m.id !== msg.id))}
          onFlyTo={(lat, lng) => flyTo(lat, lng, true)}
        />
      ))}

      {pendingPin && <PinAddSheet lat={pendingPin.lat} lng={pendingPin.lng} onSave={savePin} onClose={() => setPendingPin(null)} />}
      {selPin && <PinDetailSheet pin={selPin} onDelete={deletePin} onClose={() => setSelPin(null)} />}
      {showSend && <SendMsgSheet cLat={cLat} cLng={cLng} onSend={sendMsg} onClose={() => setShowSend(false)} />}
      {showInvite && <InviteSheet roomData={roomData} onClose={() => setShowInvite(false)} />}

      {showLeave && (
        <Sheet onClose={() => setShowLeave(false)}>
          <div style={{ fontFamily: "monospace", fontSize: 20, fontWeight: 900, color: G.accent, letterSpacing: 2, marginBottom: 6 }}>방 나가기</div>
          <div style={{ fontSize: 13, color: G.text, marginBottom: 18, lineHeight: 1.6 }}>방을 나가면 위치 공유가 중단됩니다.</div>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn v="ghost" onClick={() => setShowLeave(false)}>취소</Btn>
            <Btn v="danger" onClick={() => { send({ type: "leave", code: roomData.code, id: myInfo.id }); stopShare(); setShowLeave(false); setTimeout(() => onLeave(), 300); }}>나가기</Btn>
          </div>
        </Sheet>
      )}
    </div>
  );
}

// ── 개인정보 동의 화면 ──
function ConsentScreen({ onAgree }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#080b11", overflow: "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: "24px 20px 0" }}>
        <div style={{ fontFamily: "monospace", fontSize: 16, fontWeight: 900, color: G.accent, letterSpacing: 1, marginBottom: 6, textAlign: "center", whiteSpace: "nowrap" }}>⚠️ 개인정보 수집 및 이용 동의</div>
        <div style={{ fontSize: 11, color: G.dim, textAlign: "center", marginBottom: 24 }}>본 시스템 이용 전 반드시 읽어주세요</div>
        {[
          { title: "1. 수집하는 정보", content: "이름, 소속, 실시간 GPS 위치 정보, 메시지 및 핀 기록" },
          { title: "2. 이용 목적", content: "팀원 간 실시간 위치 공유 및 현장 상황 공유, 지휘 통제 지원" },
          { title: "3. 보관 기간", content: "방 종료 시 모든 정보 즉시 삭제됩니다. 서버에 영구 저장되지 않습니다." },
          { title: "4. ⚠️ 사용자 의무 및 처벌 규정", content: "• 업무 외 용도 사용 절대 금지\n• 타인 위치정보 외부 유출 금지\n• 위반 시 「개인정보 보호법」 제71조에 따라 5년 이하 징역 또는 5천만원 이하 벌금에 처해질 수 있습니다.\n• 직무상 알게 된 비밀 누설 시 「형법」상 공무상 비밀누설죄로 처벌받을 수 있습니다." },
          { title: "5. 동의 거부권", content: "동의를 거부할 권리가 있으나, 거부 시 본 서비스 이용이 불가합니다." },
        ].map((item) => (
          <div key={item.title} style={{ marginBottom: 16, background: G.surface2, borderRadius: 14, padding: "14px 16px", border: "1px solid " + G.border }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: G.accent, marginBottom: 8 }}>{item.title}</div>
            <div style={{ fontSize: 13, color: G.text, lineHeight: 1.7, whiteSpace: "pre-line" }}>{item.content}</div>
          </div>
        ))}
        <div style={{ background: "rgba(255,68,68,.1)", border: "1px solid rgba(255,68,68,.3)", borderRadius: 14, padding: "14px 16px", marginBottom: 24 }}>
          <div style={{ fontSize: 13, color: "#ff6b6b", fontWeight: 700, lineHeight: 1.7 }}>위 내용을 모두 읽고 이해했으며,<br /><strong>업무 목적으로만 사용</strong>할 것에 동의합니다.</div>
        </div>
      </div>
      <div style={{ padding: "16px 20px", borderTop: "1px solid " + G.border, flexShrink: 0 }}>
        <button onClick={onAgree} style={{ width: "100%", background: G.accent, color: "#000", border: "none", borderRadius: 14, padding: "16px", fontSize: 16, fontWeight: 900, cursor: "pointer", fontFamily: "inherit" }}>
          ✅ 동의하고 시작하기
        </button>
        <div style={{ textAlign: "center", fontSize: 11, color: G.dim, marginTop: 10 }}>동의하지 않으면 서비스를 이용할 수 없습니다</div>
      </div>
    </div>
  );
}

// ── 색깔 선택 ──
const COLOR_OPTIONS = [
  { color: "#00e5ff", label: "청색" },
  { color: "#00ff88", label: "녹색" },
  { color: "#ffd700", label: "황색" },
  { color: "#ff6b35", label: "주황" },
  { color: "#ff4488", label: "분홍" },
  { color: "#a78bfa", label: "보라" },
  { color: "#fb923c", label: "살구" },
  { color: "#34d399", label: "민트" },
];

function ColorPicker({ value, onChange, teamName, roomMembers }) {
  const suggestedColor = teamName
    ? (() => {
        const match = roomMembers && roomMembers.find((m) => m.team && m.team.trim() === teamName.trim() && m.color);
        return match ? match.color : null;
      })()
    : null;

  useEffect(() => {
    if (suggestedColor && suggestedColor !== value) onChange(suggestedColor);
  }, [suggestedColor]);

  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 11, letterSpacing: 1.5, color: G.dim, textTransform: "uppercase", marginBottom: 8, display: "block" }}>팀 색상</label>
      {suggestedColor && <div style={{ fontSize: 11, color: G.green, marginBottom: 8 }}>✅ 같은 팀 색상으로 자동 설정됨</div>}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {COLOR_OPTIONS.map((opt) => (
          <div key={opt.color} onClick={() => onChange(opt.color)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: "pointer" }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: opt.color + "33", border: "3px solid " + (value === opt.color ? opt.color : "transparent"), display: "flex", alignItems: "center", justifyContent: "center", transition: "all .15s" }}>
              <div style={{ width: 20, height: 20, borderRadius: 6, background: opt.color }} />
            </div>
            <div style={{ fontSize: 9, color: value === opt.color ? opt.color : G.dim, fontWeight: 700 }}>{opt.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// 공유 함수 (컴포넌트 밖)
async function shareContent(title, text, url) {
  try {
    await Share.share({ title, text, url, dialogTitle: "팀원 초대" });
    return true;
  } catch (e) {
    if (e.name === "AbortError" || (e.message && e.message.includes("cancel"))) return true;
    return false;
  }
}

// ── 초대 시트 ──
function InviteSheet({ roomData, onClose }) {
  const [copied, setCopied] = useState(false);
  // 딥링크: 앱 있으면 앱 실행, 없으면 /invite 랜딩페이지로
  const inviteLink = SERVER_URL + "/invite?c=" + roomData.code;
  const inviteText = "📡 실시간 경력 배치 초대\n\n" +
    "📱 참여 링크:\n" + inviteLink + "\n\n" +
    "🔒 비밀번호는 별도로 전달드립니다.";

  async function shareInvite() {
    try {
      await Share.share({
        title: "실시간 경력 배치 초대",
        text: "📡 실시간 경력 배치에 초대합니다.\n\n🔒 비밀번호는 별도로 전달드립니다.",
        url: inviteLink,
        dialogTitle: "팀원 초대"
      });
    } catch (e) {
      try {
        await navigator.clipboard.writeText(inviteText);
      } catch {
        const el = document.createElement("textarea");
        el.value = inviteText;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <Sheet onClose={onClose}>
      <div style={{ fontFamily: "monospace", fontSize: 20, fontWeight: 900, color: G.accent, letterSpacing: 2, marginBottom: 18 }}>🔗 팀원 초대</div>
      <div style={{ background: G.surface3, borderRadius: 14, padding: "16px", marginBottom: 12, border: "1px solid " + G.border }}>
        <div style={{ fontSize: 13, color: G.text, lineHeight: 2, whiteSpace: "pre-line", wordBreak: "break-all" }}>{inviteText}</div>
      </div>
      <div style={{ background: "rgba(0,229,255,.06)", borderRadius: 12, padding: "10px 14px", marginBottom: 16, border: "1px solid rgba(0,229,255,.2)" }}>
        <div style={{ fontSize: 11, color: G.accent, fontWeight: 700, marginBottom: 4 }}>💡 링크로 접속하면?</div>
        <div style={{ fontSize: 12, color: G.dim, lineHeight: 1.6 }}>앱이 설치된 경우 앱이 자동으로 열립니다. 앱이 없으면 설치 안내 페이지로 이동해요.</div>
      </div>
      <div style={{ fontSize: 12, color: "#ff6b6b", marginBottom: 16, lineHeight: 1.6 }}>⚠️ 비밀번호는 보안상 별도로 전달해주세요</div>
      <div style={{ display: "flex", gap: 10 }}>
        <Btn v="ghost" onClick={onClose}>닫기</Btn>
        <Btn onClick={shareInvite}>{copied ? "✅ 복사됨!" : "📤 공유하기"}</Btn>
      </div>
    </Sheet>
  );
}

function Landing({ onCreate, onJoin }) {
  const cards = [
    { i: "🛡️", t: "방 만들기", d: "관리자로 방을 생성하고 팀원을 초대", fn: onCreate },
    { i: "📍", t: "방 참여하기", d: "코드와 비밀번호로 팀에 합류", fn: onJoin },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", padding: "0 24px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle,rgba(0,229,255,.06) 0%,transparent 70%)", top: "40%", left: "50%", transform: "translate(-50%,-50%)", pointerEvents: "none" }} />
      <div style={{ fontFamily: "monospace", fontSize: 42, fontWeight: 900, letterSpacing: 3, color: G.accent, lineHeight: 1.15, marginBottom: 8, textShadow: "0 0 40px rgba(0,229,255,.35)", textAlign: "center" }}>
        실시간<br />경력 배치
      </div>
      <div style={{ fontSize: 10, letterSpacing: 5, color: "#374151", textTransform: "uppercase", marginBottom: 4, textAlign: "center" }}>REAL-TIME OFFICER DEPLOYMENT</div>
      <div style={{ fontSize: 11, color: "#1e2433", marginBottom: 40, textAlign: "center" }}>🌐 {SERVER_URL}</div>
      <div style={{ width: "100%", maxWidth: 360 }}>
        {cards.map((c) => (
          <div
            key={c.t}
            onClick={c.fn}
            style={{ background: G.surface2, border: "1px solid " + G.border, borderRadius: 18, padding: "22px 24px", cursor: "pointer", display: "flex", alignItems: "center", gap: 18, marginBottom: 14, transition: "all .2s" }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = G.accent; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = G.border; e.currentTarget.style.transform = ""; }}
          >
            <div style={{ fontSize: 32 }}>{c.i}</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{c.t}</div>
              <div style={{ fontSize: 12, color: G.dim, lineHeight: 1.5 }}>{c.d}</div>
            </div>
            <div style={{ marginLeft: "auto", color: G.dim, fontSize: 20 }}>›</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function App() {
  const [screen, setScreen] = useState("consent");
  const [modal, setModal] = useState(null);
  const [room, setRoom] = useState(null);
  const [me, setMe] = useState(null);
  const [toast, setToast] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [cf, setCf] = useState({ rn: "", pw: "", nm: "", team: "", vehicle: false, createKey: "" });
  const [jf, setJf] = useState({ code: "", pw: "", nm: "", team: "", vehicle: false });

  // FCM 푸시 알림 초기화 (앱 시작 시)
  useEffect(() => {
    initPushNotifications((token) => {
      // 토큰 도착 시 방에 있으면 바로 등록, 없으면 대기
      registerFcmToken(token, window.__currentRoomCode, window.__currentUserId);
    });
  }, []);

  // 초대 링크로 접속 시 방코드 자동입력
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inviteCode = params.get("c");
    if (inviteCode) {
      setJf((prev) => ({ ...prev, code: inviteCode.toUpperCase() }));
      // 동의 후 자동으로 참여 모달 열기
      const openJoin = () => { setErr(""); setModal("join"); };
      if (screen === "landing") openJoin();
      else {
        const unsub = setInterval(() => {
          if (screen === "landing") { openJoin(); clearInterval(unsub); }
        }, 300);
        setTimeout(() => clearInterval(unsub), 5000);
      }
    }
  }, []);

  function showToast(m) {
    setToast(m);
    setTimeout(() => setToast(null), 2600);
  }
  // FCM 포그라운드 수신 시 사용할 수 있도록 전역 등록
  useEffect(() => { window.__showToast = showToast; }, []);

  async function doCreate() {
    if (!cf.rn.trim() || cf.pw.length < 4 || !cf.nm.trim()) {
      setErr("모든 항목을 올바르게 입력하세요");
      return;
    }
    if (!cf.createKey.trim()) {
      setErr("방 생성 키를 입력하세요");
      return;
    }
    setLoading(true);
    setErr("");
    const code = genCode();
    const myId = getDeviceId();
    const myTeam = cf.team.trim() || "본부";
    const member = { id: myId, name: cf.nm.trim(), role: "admin", team: myTeam, color: cf.color || teamColor(myTeam), vehicle: cf.vehicle, lat: null, lng: null, online: true };
    try {
      const res = await apiPost("/rooms", { code: code, name: cf.rn.trim(), pwdHash: hashPwd(cf.pw), createKey: cf.createKey.trim() });
      if (res.error) {
        setErr("⚠ " + res.error);
        setLoading(false);
        return;
      }
    } catch (e) {
      setErr("⚠ 서버 연결 실패");
      setLoading(false);
      return;
    }
    const r = { id: genId(), code: code, name: cf.rn.trim(), pwdHash: hashPwd(cf.pw), members: { [myId]: member } };
    setRoom(r);
    setMe(member);
    setModal(null);
    setScreen("room");
    setCf({ rn: "", pw: "", nm: "", team: "", vehicle: false, createKey: "", color: "" });
    showToast("🚔 방이 생성되었습니다!");
    setLoading(false);
    // 전역에 방 정보 저장
    window.__currentRoomCode = code;
    window.__currentUserId = myId;
    // 토큰이 이미 있으면 즉시 등록, 없으면 토큰 도착 시 자동 등록됨
    if (window.__fcmToken) {
      registerFcmToken(window.__fcmToken, code, myId);
    } else {
      // 토큰 대기 - 최대 10초간 500ms마다 재시도
      let tries = 0;
      const poll = setInterval(() => {
        if (window.__fcmToken) {
          registerFcmToken(window.__fcmToken, code, myId);
          clearInterval(poll);
        }
        if (++tries > 20) clearInterval(poll);
      }, 500);
    }
  }

  async function doJoin() {
    if (!jf.code.trim() || !jf.pw || !jf.nm.trim()) {
      setErr("모든 항목을 입력하세요");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const code = jf.code.toUpperCase();
      const r = await apiGet("/rooms/" + code);
      if (r.error) {
        setErr("방을 찾을 수 없습니다");
        setLoading(false);
        return;
      }
      if (r.pwdHash !== hashPwd(jf.pw)) {
        setErr("비밀번호가 틀렸습니다");
        setLoading(false);
        return;
      }
      const myId = getDeviceId();
      const myTeam = jf.team.trim() || "본부";
      const chosenColor = jf.color || (() => {
        const match = Object.values(r.members || {}).find((m) => m.team && m.team.trim() === myTeam.trim() && m.color);
        return match ? match.color : teamColor(myTeam);
      })();
      const member = { id: myId, name: jf.nm.trim(), role: "member", team: myTeam, color: chosenColor, vehicle: jf.vehicle, lat: null, lng: null, online: true };
      r.members = r.members || {};
      r.members[myId] = member;
      setRoom(r);
      setMe(member);
      setModal(null);
      setScreen("room");
      setJf({ code: "", pw: "", nm: "", team: "", vehicle: false, color: "" });
      showToast("✅ " + r.name + " 입장!");
      // 전역에 방 정보 저장
      window.__currentRoomCode = code;
      window.__currentUserId = myId;
      // 토큰이 이미 있으면 즉시 등록, 없으면 토큰 도착 시 자동 등록됨
      if (window.__fcmToken) {
        registerFcmToken(window.__fcmToken, code, myId);
      } else {
        // 토큰 대기 - 최대 10초간 500ms마다 재시도
        let tries = 0;
        const poll = setInterval(() => {
          if (window.__fcmToken) {
            registerFcmToken(window.__fcmToken, code, myId);
            clearInterval(poll);
          }
          if (++tries > 20) clearInterval(poll);
        }, 500);
      }
    } catch (e) {
      setErr("서버에 연결할 수 없습니다.");
    }
    setLoading(false);
  }

  return (
    <div style={{ fontFamily: "system-ui,sans-serif", background: "#080b11", color: G.text, height: "100dvh", display: "flex", flexDirection: "column", overflow: "hidden", WebkitFontSmoothing: "antialiased", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)", boxSizing: "border-box" }}>
      {screen === "consent" && <ConsentScreen onAgree={async () => {
        setScreen("landing");
        const params = new URLSearchParams(window.location.search);
        const inviteCode = params.get("c");
        if (inviteCode) {
          setJf((prev) => ({ ...prev, code: inviteCode.toUpperCase() }));
          setTimeout(() => { setErr(""); setModal("join"); }, 300);
        }
        // 동의 후 위치 권한 + 알림 권한 한번에 요청
        setTimeout(async () => {
          // 위치 권한 요청
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(() => {}, () => {});
          }
          // 알림 권한 요청 (Capacitor)
          try {
            const { PushNotifications } = await import("@capacitor/push-notifications");
            const perm = await PushNotifications.checkPermissions();
            if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
              await PushNotifications.requestPermissions();
            }
          } catch (e) {}
        }, 500);
      }} />}
      {screen === "landing" && (
        <Landing
          onCreate={() => { setErr(""); setModal("create"); }}
          onJoin={() => { setErr(""); setModal("join"); }}
        />
      )}
      {screen === "room" && room && me && (
        <RoomScreen
          roomData={room}
          myInfo={me}
          setMyInfo={setMe}
          onLeave={() => {
            setScreen("landing");
            setRoom(null);
            setMe(null);
            showToast("👋 나갔습니다");
          }}
          showToast={showToast}
        />
      )}

      {(modal === "create" || modal === "join") && (
        <Sheet onClose={() => { setModal(null); setErr(""); }}>
          <div style={{ fontFamily: "monospace", fontSize: 22, fontWeight: 900, letterSpacing: 2, color: G.accent, marginBottom: 6 }}>
            {modal === "create" ? "방 만들기" : "방 참여하기"}
          </div>
          <div style={{ fontSize: 13, color: G.dim, marginBottom: 22, lineHeight: 1.5 }}>
            {modal === "create" ? "관리자로 새 방을 만들고 팀원을 초대합니다" : "코드와 비밀번호로 팀 위치 공유에 참여합니다"}
          </div>

          {modal === "create" && (
            <>
              <Field label="방 생성 키" type="password" placeholder="방 생성 키는 관리자에게 문의" value={cf.createKey} onChange={(e) => setCf({ ...cf, createKey: e.target.value })} />
              <Field label="방 이름" placeholder="집회, 경호 등" value={cf.rn} onChange={(e) => setCf({ ...cf, rn: e.target.value })} />
              <Field label="비밀번호 (4자 이상)" type="password" placeholder="••••••" value={cf.pw} onChange={(e) => setCf({ ...cf, pw: e.target.value })} />
              <Field label="내 이름" placeholder="홍길동" value={cf.nm} onChange={(e) => setCf({ ...cf, nm: e.target.value })} />
              <Field label="소속 (선택)" placeholder="1기동대,서경비계" value={cf.team} onChange={(e) => setCf({ ...cf, team: e.target.value })} />
              <ColorPicker value={cf.color || COLORS[0]} onChange={(c) => setCf({ ...cf, color: c })} teamName={cf.team} roomMembers={[]} />
              <VehicleToggle val={cf.vehicle} onChange={() => setCf({ ...cf, vehicle: !cf.vehicle })} />
            </>
          )}

          {modal === "join" && (
            <>
              <Field label="방 코드" placeholder="ABC123" value={jf.code} onChange={(e) => setJf({ ...jf, code: e.target.value.toUpperCase() })} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
              <Field label="비밀번호" type="password" placeholder="••••••" value={jf.pw} onChange={(e) => setJf({ ...jf, pw: e.target.value })} />
              <Field label="내 이름" placeholder="홍길동" value={jf.nm} onChange={(e) => setJf({ ...jf, nm: e.target.value })} />
              <Field label="소속 (선택)" placeholder="1기동대, 2기동대, 본부" value={jf.team} onChange={(e) => setJf({ ...jf, team: e.target.value })} />
              <ColorPicker value={jf.color || COLORS[0]} onChange={(c) => setJf({ ...jf, color: c })} teamName={jf.team} roomMembers={jf.roomMembers || []} />
              <VehicleToggle val={jf.vehicle} onChange={() => setJf({ ...jf, vehicle: !jf.vehicle })} />
            </>
          )}

          {err && <div style={{ color: "#ff5555", fontSize: 13, marginBottom: 12 }}>⚠ {err}</div>}

          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <Btn v="ghost" onClick={() => { setModal(null); setErr(""); }}>취소</Btn>
            <Btn disabled={loading} onClick={modal === "create" ? doCreate : doJoin}>
              {loading ? "연결 중..." : modal === "create" ? "방 생성" : "입장하기"}
            </Btn>
          </div>
        </Sheet>
      )}

      {toast && (
        <div style={{ position: "fixed", bottom: 90, left: "50%", transform: "translateX(-50%)", background: G.surface2, border: "1px solid " + G.accent, color: G.accent, padding: "11px 24px", borderRadius: 30, fontSize: 13, fontWeight: 700, zIndex: 9999, whiteSpace: "nowrap", pointerEvents: "none", boxShadow: "0 4px 20px rgba(0,229,255,.2)" }}>
          {toast}
        </div>
      )}
    </div>
  );
}
